import React, { useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2, AlertTriangle, CheckCircle2, X, FileSpreadsheet, Trash2, Plus, MapPin } from 'lucide-react';
import { useAuth, API } from '../../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

/**
 * Two-step import:
 *  1) Upload xlsx -> backend parses -> editable preview rows.
 *  2) Edit / deselect / confirm -> backend creates/overwrites rides.
 */
export default function RidesImportDialog({ open, onOpenChange, partners = [], partnerNames = [], onImported }) {
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  // step: 'upload' | 'preview' | 'submitting'
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importProgress, setImportProgress] = useState(null); // {done, total}
  const [rows, setRows] = useState([]); // editable preview rows
  const [selected, setSelected] = useState({}); // { rowKey: bool }
  const [availableExtras, setAvailableExtras] = useState([]);
  // Selected partner is encoded as either "acct::<id>" or "name::<name>"
  const [partnerKey, setPartnerKey] = useState('');
  const [stats, setStats] = useState({ total: 0, duplicates: 0 });

  const selectedPartner = useMemo(() => {
    if (!partnerKey) return null;
    if (partnerKey.startsWith('acct::')) {
      const id = partnerKey.slice(6);
      const p = partners.find((x) => x.id === id);
      return p ? { kind: 'account', id: p.id, name: p.company_name || p.name || p.email } : null;
    }
    if (partnerKey.startsWith('name::')) {
      const name = partnerKey.slice(6);
      return { kind: 'name', id: null, name };
    }
    return null;
  }, [partnerKey, partners]);

  const reset = () => {
    setStep('upload');
    setFile(null);
    setRows([]);
    setSelected({});
    setAvailableExtras([]);
    setPartnerKey('');
    setStats({ total: 0, duplicates: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const n = f.name.toLowerCase();
    if (!n.endsWith('.xlsx') && !n.endsWith('.xlsm') && !n.endsWith('.xls') && !n.endsWith('.pdf')) {
      toast.error('Please select an .xlsx, .xls or .pdf file');
      return;
    }
    setFile(f);
  };

  const handleParse = async () => {
    if (!file) {
      toast.error('Select a file first');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Include partner_company so the backend applies matching pricing rules and
      // returns per-row `price_from_rule` / `pricing_rule_matched` metadata.
      // partner_company uses the partner name shown on rides (e.g. "MOZIO"), not the
      // partner-account UUID — pricing rules are keyed by that string.
      const partnerCompany = selectedPartner?.name || '';
      if (partnerCompany) {
        fd.append('partner_company', partnerCompany);
      }
      const res = await axios.post(`${API}/rides/import/parse`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      });
      const parsedRows = (res.data?.rows || []).map((r, idx) => ({
        ...r,
        _key: `${r._row || idx}-${r.booking_number || idx}`,
      }));
      if (!parsedRows.length) {
        toast.error('No rides found in the file');
        setUploading(false);
        return;
      }
      setRows(parsedRows);
      // Surface pricing rule summary as an info toast
      if (partnerCompany) {
        const matched = parsedRows.filter(r => r.pricing_rule_matched).length;
        const missing = parsedRows.length - matched;
        if (matched > 0) {
          toast.success(`Auto-priced ${matched}/${parsedRows.length} rides from partner tariff${missing ? ` · ${missing} without a matching rule` : ''}`);
        } else if (parsedRows.some(r => r.pricing_rule_hint)) {
          toast.warning(`No pricing rule matched — check the rules for ${partnerCompany}`);
        }
      }
      setSelected(Object.fromEntries(parsedRows.map((r) => [r._key, true])));
      setStats({ total: res.data?.total || 0, duplicates: res.data?.duplicates || 0 });
      setAvailableExtras(Array.isArray(res.data?.available_extras) ? res.data.available_extras : []);
      setStep('preview');
      toast.success(`Parsed ${parsedRows.length} rides`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to parse file';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, [field]: value } : r)));
  };

  const removeRow = (key) => {
    setRows((prev) => prev.filter((r) => r._key !== key));
    setSelected((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelected(Object.fromEntries(rows.map((r) => [r._key, !!checked])));
  };

  const selectedCount = useMemo(
    () => rows.filter((r) => selected[r._key]).length,
    [rows, selected],
  );

  const handleConfirm = async () => {
    if (!selectedPartner) {
      toast.error('Please select a partner');
      return;
    }
    if (selectedCount === 0) {
      toast.error('No rides selected');
      return;
    }
    setSubmitting(true);
    try {
      const ridesPayload = rows
        .filter((r) => selected[r._key])
        .map((r) => {
          // strip preview-only fields, ensure numbers are numbers
          const { _key, _row, _status_external, _duplicate, ...rest } = r;
          return {
            ...rest,
            price: rest.price === '' || rest.price === null ? null : Number(rest.price),
            number_of_people:
              rest.number_of_people === '' || rest.number_of_people === null
                ? null
                : Number(rest.number_of_people),
            payment_to_partner:
              rest.payment_to_partner === '' || rest.payment_to_partner === null
                ? null
                : Number(rest.payment_to_partner),
          };
        });
      // Import in batches of 25 with a live progress bar (also avoids proxy timeouts on huge files)
      const BATCH = 25;
      let created = 0;
      let overwritten = 0;
      let skipped = [];
      setImportProgress({ done: 0, total: ridesPayload.length });
      for (let i = 0; i < ridesPayload.length; i += BATCH) {
        const chunk = ridesPayload.slice(i, i + BATCH);
        const res = await axios.post(
          `${API}/rides/import/confirm`,
          {
            partner_id: selectedPartner.kind === 'account' ? selectedPartner.id : null,
            partner_name: selectedPartner.name,
            rides: chunk,
          },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 },
        );
        created += res.data?.created || 0;
        overwritten += res.data?.overwritten || 0;
        skipped = skipped.concat(res.data?.skipped || []);
        setImportProgress({ done: Math.min(i + BATCH, ridesPayload.length), total: ridesPayload.length });
      }
      toast.success(
        `Import complete — Created: ${created}, Overwritten: ${overwritten}${
          skipped.length ? `, Skipped: ${skipped.length}` : ''
        }`,
      );
      if (onImported) onImported({ created, overwritten });
      handleClose(false);
    } catch (err) {
      const msg = err.code === 'ECONNABORTED'
        ? 'Import is taking too long - the server may still be processing. Refresh Rides in a minute before retrying, to avoid duplicates.'
        : (err.response?.data?.detail || err.message || 'Failed to import rides');
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setImportProgress(null);
    }
  };

  // For the datetime-local input we need YYYY-MM-DDTHH:mm
  const isoToLocalInput = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
        d.getHours(),
      )}:${pad(d.getMinutes())}`;
    } catch {
      return '';
    }
  };

  const localInputToIso = (localVal) => {
    if (!localVal) return null;
    try {
      return new Date(localVal).toISOString();
    } catch {
      return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="rides-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
            Import Rides from Partner File
          </DialogTitle>
          <DialogDescription>
            Upload a partner's Excel (.xlsx / .xls) or PDF file. We parse the rows so you can review,
            edit and confirm before creating the rides. Existing bookings with the same code will
            be overwritten.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-6 py-4" data-testid="rides-import-upload-step">
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">Partner</Label>
              <Select value={partnerKey} onValueChange={setPartnerKey}>
                <SelectTrigger data-testid="rides-import-partner-select">
                  <SelectValue placeholder="Select the partner this file belongs to" />
                </SelectTrigger>
                <SelectContent>
                  {partnerNames.length > 0 && (
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      Partner names
                    </div>
                  )}
                  {partnerNames.map((name) => (
                    <SelectItem key={`name::${name}`} value={`name::${name}`}>
                      {name}
                    </SelectItem>
                  ))}
                  {partners.length > 0 && (
                    <div className={`px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold ${partnerNames.length > 0 ? 'border-t border-slate-100 mt-1 pt-2' : ''}`}>
                      Partner accounts (portal)
                    </div>
                  )}
                  {partners.map((p) => (
                    <SelectItem key={`acct::${p.id}`} value={`acct::${p.id}`}>
                      {p.company_name || p.name || p.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                All imported rides will be assigned to this partner.
              </p>
            </div>
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">File (.xlsx, .xls or .pdf)</Label>
              <div className="flex items-center gap-3">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xlsm,.xls,.pdf"
                  onChange={handleFileChange}
                  data-testid="rides-import-file-input"
                  className="cursor-pointer"
                />
                {file && (
                  <Badge variant="secondary" className="whitespace-nowrap">
                    {file.name}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Excel (.xlsx / legacy .xls) or PDF table with these columns: Code, Status, Origin, Destination,
                Pickup, Passengers, Bags, Flight, Fare, Traveller First/Last Name, Driver Comments,
                Baby/Booster/Child seat, Meet &amp; Greet, Special Luggage, Extra Stop, Pets.
              </p>
              <div className="text-xs text-slate-500 mt-2 flex items-start gap-1.5 bg-sky-50 border border-sky-100 rounded p-2">
                <MapPin className="h-3.5 w-3.5 text-sky-600 flex-shrink-0 mt-0.5" />
                <span>Pickup &amp; dropoff addresses are <span className="font-medium">geocoded automatically</span> in the background after import (uses OpenStreetMap, ~1 sec per address).</span>
              </div>
            </div>
            <DialogFooter className="!justify-between">
              <Button variant="outline" onClick={() => handleClose(false)} data-testid="rides-import-cancel-upload">
                Cancel
              </Button>
              <Button
                onClick={handleParse}
                disabled={!file || !partnerKey || uploading}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="rides-import-parse-button"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Parsing…
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Parse file
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'preview' && (
          <div className="flex-1 flex flex-col overflow-hidden" data-testid="rides-import-preview-step">
            <div className="flex flex-wrap items-center gap-3 px-1 py-2 text-sm">
              <Badge variant="secondary" className="text-sm">
                {selectedCount} / {rows.length} selected
              </Badge>
              {stats.duplicates > 0 && (
                <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-sm">
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                  {stats.duplicates} duplicate{stats.duplicates !== 1 ? 's' : ''} (will overwrite)
                </Badge>
              )}
              <span className="text-slate-500">
                Partner: <span className="font-medium text-slate-700">
                  {selectedPartner?.name || '—'}
                </span>
                {selectedPartner?.kind === 'account' && (
                  <span className="ml-1 text-[10px] uppercase tracking-wide bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded">Account</span>
                )}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAll(true)}
                  data-testid="rides-import-select-all"
                >
                  Select all
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAll(false)}
                  data-testid="rides-import-deselect-all"
                >
                  Deselect all
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto border rounded-md" data-testid="rides-import-preview-table">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-2 py-2 font-semibold text-slate-700">Booking #</th>
                    <th className="px-2 py-2 font-semibold text-slate-700">Pickup (Athens)</th>
                    <th className="px-2 py-2 font-semibold text-slate-700">Customer</th>
                    <th className="px-2 py-2 font-semibold text-slate-700">Pickup location</th>
                    <th className="px-2 py-2 font-semibold text-slate-700">Dropoff location</th>
                    <th className="px-2 py-2 font-semibold text-slate-700">Pax</th>
                    <th className="px-2 py-2 font-semibold text-slate-700">Flight</th>
                    <th className="px-2 py-2 font-semibold text-slate-700">Price (€)</th>
                    <th className="px-2 py-2 font-semibold text-slate-700">Extras</th>
                    <th className="px-2 py-2 font-semibold text-slate-700">Notes</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isSelected = !!selected[r._key];
                    return (
                      <tr
                        key={r._key}
                        className={`border-t align-top ${
                          r._duplicate ? 'bg-amber-50/50' : ''
                        } ${!isSelected ? 'opacity-40' : ''}`}
                        data-testid={`rides-import-row-${r._key}`}
                      >
                        <td className="px-2 py-2">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(v) =>
                              setSelected((prev) => ({ ...prev, [r._key]: !!v }))
                            }
                            data-testid={`rides-import-row-checkbox-${r._key}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            className="h-8 text-xs"
                            value={r.booking_number || ''}
                            onChange={(e) => updateRow(r._key, 'booking_number', e.target.value)}
                          />
                          {r._duplicate && (
                            <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" /> overwrite
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="datetime-local"
                            className="h-8 text-xs"
                            value={isoToLocalInput(r.date_time)}
                            onChange={(e) =>
                              updateRow(r._key, 'date_time', localInputToIso(e.target.value))
                            }
                          />
                        </td>
                        <td className="px-2 py-2 min-w-[150px]">
                          <Input
                            className="h-8 text-xs"
                            value={r.customer_name || ''}
                            onChange={(e) => updateRow(r._key, 'customer_name', e.target.value)}
                          />
                        </td>
                        <td className="px-2 py-2 min-w-[200px]">
                          <Textarea
                            className="text-xs min-h-[32px]"
                            rows={1}
                            value={r.pickup_location || ''}
                            onChange={(e) => updateRow(r._key, 'pickup_location', e.target.value)}
                          />
                        </td>
                        <td className="px-2 py-2 min-w-[200px]">
                          <Textarea
                            className="text-xs min-h-[32px]"
                            rows={1}
                            value={r.dropoff_location || ''}
                            onChange={(e) => updateRow(r._key, 'dropoff_location', e.target.value)}
                          />
                        </td>
                        <td className="px-2 py-2 w-16">
                          <Input
                            type="number"
                            className="h-8 text-xs"
                            value={r.number_of_people ?? ''}
                            onChange={(e) =>
                              updateRow(
                                r._key,
                                'number_of_people',
                                e.target.value === '' ? null : Number(e.target.value),
                              )
                            }
                          />
                        </td>
                        <td className="px-2 py-2 w-24">
                          <Input
                            className="h-8 text-xs"
                            value={r.flight_number || ''}
                            onChange={(e) => updateRow(r._key, 'flight_number', e.target.value)}
                          />
                        </td>
                        <td className="px-2 py-2 w-28">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              step="0.01"
                              className={`h-8 text-xs ${r.price_from_rule ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : (r.pricing_rule_hint ? 'bg-amber-50 border-amber-300' : '')}`}
                              value={r.price ?? ''}
                              onChange={(e) =>
                                updateRow(
                                  r._key,
                                  'price',
                                  e.target.value === '' ? null : Number(e.target.value),
                                )
                              }
                              title={
                                r.price_from_rule
                                  ? `Auto-filled from tariff (${r.pricing_rule_origin_area} → ${r.pricing_rule_destination_area})`
                                  : r.pricing_rule_hint || undefined
                              }
                            />
                            {r.price_from_rule && (
                              <span className="text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium" title="From partner tariff">€✓</span>
                            )}
                            {!r.price_from_rule && r.pricing_rule_hint && (
                              <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-medium" title={r.pricing_rule_hint}>?</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 max-w-[200px]">
                          <ExtrasMultiSelect
                            value={r.extras || []}
                            options={availableExtras}
                            onChange={(next) => updateRow(r._key, 'extras', next)}
                            isAirport={r.is_airport_pickup}
                            rowKey={r._key}
                          />
                        </td>
                        <td className="px-2 py-2 min-w-[180px]">
                          <Textarea
                            className="text-xs min-h-[32px]"
                            rows={1}
                            value={r.notes || ''}
                            onChange={(e) => updateRow(r._key, 'notes', e.target.value)}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-600 hover:text-red-700"
                            onClick={() => removeRow(r._key)}
                            data-testid={`rides-import-row-remove-${r._key}`}
                            title="Remove row"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <DialogFooter className="pt-3 !justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  setStep('upload');
                }}
                data-testid="rides-import-back-button"
              >
                <X className="mr-2 h-4 w-4" /> Back
              </Button>
              {submitting && importProgress && (
                <div className="flex-1 min-w-[180px] mr-3" data-testid="import-progress">
                  <div className="flex justify-between text-xs text-slate-600 mb-1">
                    <span>Importing rides…</span>
                    <span data-testid="import-progress-count">{importProgress.done} / {importProgress.total}</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                      style={{ width: `${Math.round((importProgress.done / Math.max(importProgress.total, 1)) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
              <Button
                onClick={handleConfirm}
                disabled={submitting || selectedCount === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="rides-import-confirm-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Import {selectedCount} ride{selectedCount !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ExtrasMultiSelect({ value = [], options = [], onChange, isAirport, rowKey }) {
  const [open, setOpen] = useState(false);
  const [newExtra, setNewExtra] = useState('');
  // Merge any value items not already in options so they remain visible
  const allOptions = useMemo(() => {
    const set = new Set(options);
    value.forEach((v) => set.add(v));
    return Array.from(set);
  }, [options, value]);

  const toggle = (name) => {
    if (value.includes(name)) {
      onChange(value.filter((v) => v !== name));
    } else {
      onChange([...value, name]);
    }
  };

  const addCustom = () => {
    const n = newExtra.trim();
    if (!n) return;
    if (!value.includes(n)) onChange([...value, n]);
    setNewExtra('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex flex-wrap gap-1 min-h-[28px] w-full rounded border border-slate-200 px-1.5 py-1 hover:border-slate-400 hover:bg-slate-50 transition-colors text-left"
          data-testid={`extras-picker-${rowKey}`}
        >
          {value.length === 0 && !isAirport && (
            <span className="text-[10px] text-slate-400 italic">click to add</span>
          )}
          {value.map((e) => (
            <Badge key={e} variant="secondary" className="text-[10px] px-1.5 py-0">
              {e}
            </Badge>
          ))}
          {isAirport && (
            <Badge className="bg-sky-100 text-sky-800 text-[10px] px-1.5 py-0 border border-sky-200">
              Airport
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        data-testid={`extras-picker-popover-${rowKey}`}
      >
        <div className="max-h-56 overflow-y-auto py-1">
          {allOptions.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400 italic">No extras available</div>
          )}
          {allOptions.map((name) => {
            const checked = value.includes(name);
            return (
              <label
                key={name}
                className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-100 cursor-pointer"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(name)}
                  data-testid={`extras-picker-option-${rowKey}-${name}`}
                />
                <span className="flex-1">{name}</span>
              </label>
            );
          })}
        </div>
        <div className="border-t border-slate-100 p-2 flex items-center gap-1">
          <Input
            value={newExtra}
            onChange={(e) => setNewExtra(e.target.value)}
            placeholder="Add custom…"
            className="h-7 text-xs"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustom();
              }
            }}
            data-testid={`extras-picker-new-input-${rowKey}`}
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-emerald-600"
            onClick={addCustom}
            disabled={!newExtra.trim()}
            data-testid={`extras-picker-add-${rowKey}`}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
